'use strict';

import { updateUIFromShadowRoot } from '@core/i18n/window-language';
import {
    globalSharedStyles,
    typographySharedStyles,
    customTipsStyles,
} from '@core/styles';
import { UPDATE_LANGUAGE_EVENT } from '@core/constants/EVENTS';
import {
    handleCloseButtonsEvent,
    dispatchCloseTutorialEvent,
    shouldCloseOnMouseLeave,
} from './utils.js';

import htmlContent from './DefaultHomeIconTutorialComponent.html';
import cssContent from './DefaultHomeIconTutorialComponent.css';

/**
 * DefaultHomeIconTips - A tutorial component that provides tips for the default home icon functionality.
 * Displays instructions and guidance for using the home icon feature to set default views.
 *
 * @class DefaultHomeIconTips
 * @extends {HTMLElement}
 */
class DefaultHomeIconTips extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        /** @type {HTMLTemplateElement} */
        const template = document.createElement('template');
        template.innerHTML = htmlContent;

        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(cssContent);

        this.shadowRoot.adoptedStyleSheets = [
            globalSharedStyles,
            typographySharedStyles,
            customTipsStyles,
            styleSheet,
        ];
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        // Initialize DOM element references
        this._understandButton = null;
        this._closeButton = null;
        this._searchTipsContainer = null;
        this._mouseLeaveTimeout = null;

        // Bind event handlers
        this._handleTranslationHelperBound = this._handleTranslationHelper.bind(this);
        this._handleUnderstandClickBound = this._handleUnderstandClick.bind(this);
        this._handleCloseClickBound = this._handleCloseClick.bind(this);
        this._handleMouseLeaveBound = this._handleMouseLeave.bind(this);
    }

    connectedCallback() {
        this._cacheDOMElements();
        this._addEventListeners();
        this._handleTranslationHelper();
    }

    /**
     * Cache DOM element references for better performance.
     * @returns {void}
     * @private
     */
    _cacheDOMElements() {
        this._understandButton = this.shadowRoot.querySelector(
            '[data-understand-button]',
        );
        this._closeButton = this.shadowRoot.querySelector(
            '[data-search-tip-close-button]',
        );
        this._searchTipsContainer = this.shadowRoot.querySelector('.search-tips');
    }

    /**
     * Handle the understand button click event.
     * @returns {void}
     * @private
     */
    _handleUnderstandClick() {
        handleCloseButtonsEvent(() => this.close());
    }

    /**
     * Handle the close button click event.
     * @returns {void}
     * @private
     */
    _handleCloseClick() {
        handleCloseButtonsEvent(() => this.close());
    }

    /**
     * Handle the mouse leave event for the search tips container.
     * @param {MouseEvent} event - The mouse event
     * @returns {void}
     * @private
     */
    _handleMouseLeave(event) {
        if (!this._searchTipsContainer) return;

        const bounds = this._searchTipsContainer.getBoundingClientRect();

        if (shouldCloseOnMouseLeave(event, bounds)) {
            this.close();
        }
    }

    /**
     * Translate the component elements.
     * @returns {void}
     * @private
     */
    _handleTranslationHelper() {
        updateUIFromShadowRoot(this.shadowRoot);
    }

    /**
     * Add event listeners to the component elements.
     * - Update the UI when the language changes
     * - Handle button clicks
     * - Handle mouse leave events
     * @returns {void}
     * @private
     */
    _addEventListeners() {
        // Language update listener
        document.addEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );

        // Button click listeners
        if (this._understandButton) {
            this._understandButton.addEventListener(
                'click',
                this._handleUnderstandClickBound,
            );
        }

        if (this._closeButton) {
            this._closeButton.addEventListener('click', this._handleCloseClickBound);
        }
    }

    /**
     * Add the mouse leave event to the search tips container.
     * This method can be called externally to enable mouse leave behavior.
     * @returns {void}
     */
    addMouseLeaveEvent() {
        if (this._searchTipsContainer) {
            this._searchTipsContainer.addEventListener(
                'mouseleave',
                this._handleMouseLeaveBound,
            );
        }
    }

    /**
     * Close the tutorial component and dispatch close event.
     * @returns {void}
     */
    close() {
        dispatchCloseTutorialEvent();
    }

    /**
     * Clean up event listeners and references to prevent memory leaks.
     * @returns {void}
     */
    disconnectedCallback() {
        // Clear any pending timeouts
        if (this._mouseLeaveTimeout) {
            clearTimeout(this._mouseLeaveTimeout);
            this._mouseLeaveTimeout = null;
        }

        // Remove global event listeners
        document.removeEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );

        // Remove component event listeners
        if (this._understandButton) {
            this._understandButton.removeEventListener(
                'click',
                this._handleUnderstandClickBound,
            );
        }

        if (this._closeButton) {
            this._closeButton.removeEventListener('click', this._handleCloseClickBound);
        }

        if (this._searchTipsContainer) {
            this._searchTipsContainer.removeEventListener(
                'mouseleave',
                this._handleMouseLeaveBound,
            );
        }

        // Clear DOM references
        this._understandButton = null;
        this._closeButton = null;
        this._searchTipsContainer = null;
    }
}

customElements.define('default-home-icon-tips', DefaultHomeIconTips);
